// File: src/English/PoetryModule.js

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { shuffleArray } from "./EnglishTool/shuffleArray.js";

// Reuse your existing quiz-building helpers
import {
  buildPoeticDeviceMC,
  buildIdentifyPoeticDeviceQ,
  buildRhymeSchemeMC,
  buildPoetryFormMC,
  buildLineStanzaMC,
  buildIambicPentameterMC
} from "./EnglishTool/AdvanceEnglishHelper";

import { poetryFormsPool } from "./EnglishTool/pool/poetryFormsPool.js";
import { poeticDevicesPool, identifyPoeticDevicesPool } from "./EnglishTool/pool/poeticDevicesPool.js";
import { rhymeSchemePool } from "./EnglishTool/pool/ryhmeSchemePool.js";
import { lineStanzaPool } from "./EnglishTool/pool/lineStanzaPool.js";
import { iambicPentameterPool } from "./EnglishTool/pool/iambicPentameterPool.js";

// Import your existing quiz UI components
import {
  StoryModeMultipleChoice,
  StoryModeFillInBlank,
  StoryModeIdentify,
  StoryModeMultiPart,
  FinalQuiz    // We will keep and use this!
} from "./EnglishTool/StoryModeStructure";

import SpeakButton from "./EnglishTool/SpeakButton";


// ==================== ENRICHED POETRY CURRICULUM ==================== //
//
// You can expand each grade with multiple units as you like.
// Here's a more fleshed-out Grade 8 Unit 1 with extra slides.
const poetryCurriculum = {
  grade8: {
    units: [
      {
        title: "Grade 8 Poetry Unit 1: Introduction to Poetry Forms & Devices",
        slides: [
          {
            title: "What is Poetry?",
            body: (
              <>
                <p>
                  Poetry expresses ideas, emotions, and images in creative ways, 
                  often appealing to our senses and imaginations. 
                  It can follow strict forms or break the rules entirely.
                </p>
                <p>
                  <b>Key Idea:</b> Poems aren’t just pretty words—poets choose language deliberately 
                  to convey deep meaning, evoke emotion, or paint vivid mental pictures.
                </p>
                <p>
                  <b>Example:</b> <i>"The sun rises, painting the sky with golden words."</i>{" "}
                  <SpeakButton text="The sun rises, painting the sky with golden words." />
                </p>
              </>
            )
          },
          {
            title: "Haiku: Nature in Three Lines",
            body: (
              <>
                <p>
                  <b>Haiku</b> is a classic Japanese poetry form with a strict structure:
                </p>
                <ul>
                  <li>3 lines total</li>
                  <li>Syllable pattern: <b>5-7-5</b></li>
                  <li>Often focuses on nature or a single moment in time</li>
                </ul>
                <blockquote>
                  An old silent pond...<br />
                  A frog jumps into the pond,<br />
                  Splash! Silence again.
                </blockquote>
                <SpeakButton text="An old silent pond. A frog jumps into the pond, splash! Silence again." />
              </>
            )
          },
          {
            title: "Free Verse: No Rules, Just Expression",
            body: (
              <>
                <p>
                  <b>Free Verse</b> ignores traditional rhyme or meter. Instead, it’s driven by the poet’s 
                  personal rhythms, line breaks, and creative expression. 
                </p>
                <blockquote>
                  The wind whispers<br />
                  through empty streets,<br />
                  carrying secrets<br />
                  under the moonlight.
                </blockquote>
                <SpeakButton text="The wind whispers through empty streets, carrying secrets under the moonlight." />
                <p>
                  <b>Tip:</b> Even though there’s no formal structure, free verse still aims 
                  to be impactful with choice of words, imagery, and flow.
                </p>
              </>
            )
          },
          {
            title: "Couplet: Two Rhyming Lines",
            body: (
              <>
                <p>
                  A <b>couplet</b> is two consecutive lines with the same length, ending in rhyme. 
                  Couplets can stand alone or form part of a larger poem.
                </p>
                <blockquote>
                  The sky was clear, the stars were bright,<br />
                  We watched the moon glow all the night.
                </blockquote>
                <SpeakButton text="The sky was clear, the stars were bright. We watched the moon glow all the night." />
                <p>
                  <b>Challenge:</b> Try writing a couplet that describes how you feel at sunrise!
                </p>
              </>
            )
          },
          {
            title: "Rhyme, Rhythm, and Meter",
            body: (
              <>
                <p>
                  <b>Rhyme</b> means having similar ending sounds (<i>cat/hat, sky/fly</i>). 
                  <b>Rhythm</b> (or <b>meter</b>) refers to a pattern of stressed and unstressed syllables.
                </p>
                <blockquote>
                  Twinkle, twinkle, little star,<br />
                  How I wonder what you are.
                </blockquote>
                <SpeakButton text="Twinkle, twinkle, little star, How I wonder what you are." />
                <p>
                  <b>Example Meter:</b> Iambic pentameter has five iambs (unstressed/stressed) per line.
                </p>
              </>
            )
          },
          {
            title: "Imagery: Painting with Words",
            body: (
              <>
                <p>
                  <b>Imagery</b> uses descriptive language to engage senses (sight, sound, smell, taste, touch). 
                  It helps readers experience the poem as if they’re “inside” it.
                </p>
                <blockquote>The golden sun melted over the icy blue lake.</blockquote>
                <SpeakButton text="The golden sun melted over the icy blue lake." />
                <p>
                  <b>Practice:</b> Write a single line describing a quiet evening using at least one color and one sound.
                </p>
              </>
            )
          },
          {
            title: "Using Poetic Devices",
            body: (
              <>
                <p>
                  Poets often employ devices like:
                  <ul>
                    <li><b>Alliteration:</b> Repeating initial consonant sounds (“She sells seashells…”).</li>
                    <li><b>Personification:</b> Giving human qualities to non-human things (“The rain danced…”).</li>
                    <li><b>Metaphor:</b> Direct comparison (“Life is a journey.”)</li>
                    <li><b>Simile:</b> Comparison using “like” or “as” (“Her smile was like sunshine.”)</li>
                  </ul>
                </p>
              </>
            )
          },
          {
            title: "Practice: Identify Poetry Forms & Devices",
            body: (
              <>
                <p>
                  <b>Question 1:</b> Which poetry form are these lines mimicking?
                  <br />
                  <i>
                    Shadows stretch across the lawn,<br />
                    Crickets start to sing their song.
                  </i>
                  <SpeakButton text="Shadows stretch across the lawn, Crickets start to sing their song." />
                </p>
                <p>
                  <b>Question 2:</b> Which poetic device is used in: <i>"Leaves danced in the breeze"?</i>
                </p>
              </>
            )
          }
        ],

        anthology: [
          {
            title: "Classic Haiku",
            lines: [
              "Old pond—",
              "A frog jumps in,",
              "The sound of water."
            ],
            author: "Matsuo Bashō"
          },
          {
            title: "Couplet Example",
            lines: [
              "Morning light creeps through my door,",
              "Softly sweeping across the floor."
            ],
            author: "Student example"
          },
          {
            title: "Free Verse Example",
            lines: [
              "Rain falls in silver threads,",
              "Erasing the city’s sharp edges.",
              "People rush beneath umbrellas—",
              "Silent, except for the tapping drops."
            ],
            author: "Student example"
          }
        ],

        // Each "unit" can generate its own quiz:
        generateQuiz: () => {
          const questionPool = [
            buildPoetryFormMC(poetryFormsPool),          // "Which form is this poem written in?"
            buildPoeticDeviceMC(poeticDevicesPool),      // "Which device is used in: ...?"
            buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
            buildLineStanzaMC(lineStanzaPool),
            buildIambicPentameterMC(iambicPentameterPool),
            buildRhymeSchemeMC(rhymeSchemePool),
            // Example open-ended question:
            {
              questionType: "FITB",
              question: "Write a two-line couplet about the weather (both lines must rhyme).",
              correctAnswer: null,
              checkType: "rhyme-couplet",
              requiredWord: null
            },
            // Another multiple choice:
            {
              questionType: "MC",
              question: 'Which poetic device is used in: "The sun smiled on the fields"?',
              correctAnswer: "Personification",
              options: ["Metaphor", "Personification", "Alliteration", "Onomatopoeia"]
            }
          ].filter(Boolean);

          // Return up to 6 random questions
          return shuffleArray(questionPool).slice(0, 6);
        }
      }
    ]
  },

  grade9: {
    units: [
      {
        title: "Grade 9 Poetry Unit 1: Extended Metaphor, Sonnet & Critical Analysis",
        slides: [
          {
            title: "What Makes Poetry Powerful?",
            body: (
              <>
                <p>
                  <b>Grade 9 Poetry:</b> Go beyond surface meaning—explore structure, subtlety, and powerful imagery.
                </p>
                <p>
                  <b>Key Focus:</b> How do form and language combine to create lasting emotional impact?
                </p>
                <p>
                  <b>Example:</b> <i>
                    "Hope is the thing with feathers / That perches in the soul..." 
                  </i> 
                  <SpeakButton text="Hope is the thing with feathers that perches in the soul." />
                  <br />
                  <i>—Emily Dickinson (extended metaphor)</i>
                </p>
              </>
            )
          },
          {
            title: "Extended Metaphor: Layered Meaning",
            body: (
              <>
                <p>
                  <b>Extended Metaphor:</b> A sustained comparison, often running through several lines or the whole poem. Used for depth and complexity.
                </p>
                <blockquote>
                  "All the world's a stage,<br/>
                  And all the men and women merely players..."<br/>
                  <i>—Shakespeare, As You Like It</i>
                </blockquote>
                <SpeakButton text="All the world's a stage, and all the men and women merely players." />
                <p>
                  <b>Analysis:</b> The speaker compares life to a play, suggesting people have roles and scripts.
                </p>
                <p>
                  <b>Challenge:</b> Find an extended metaphor in a favorite song or poem. What abstract idea is being explored?
                </p>
              </>
            )
          },
          {
            title: "The Shakespearean Sonnet",
            body: (
              <>
                <p>
                  <b>Sonnet:</b> A 14-line poem, traditionally about love or philosophical ideas, written in iambic pentameter.
                </p>
                <ul>
                  <li><b>Rhyme Scheme:</b> ABAB CDCD EFEF GG (English/Shakespearean sonnet)</li>
                  <li><b>Volta:</b> The "turn" or shift in tone, often at line 9 or 13</li>
                </ul>
                <blockquote>
                  "Shall I compare thee to a summer's day?<br />
                  Thou art more lovely and more temperate..."<br />
                  <i>—William Shakespeare, Sonnet 18</i>
                </blockquote>
                <SpeakButton text="Shall I compare thee to a summer's day? Thou art more lovely and more temperate." />
                <p>
                  <b>Practice:</b> Identify the volta and explain how the mood changes.
                </p>
              </>
            )
          },
          {
            title: "Imagery, Symbolism, and Allusion",
            body: (
              <>
                <p>
                  <b>Imagery:</b> Vivid language appealing to the senses; brings abstract ideas to life.
                </p>
                <p>
                  <b>Symbolism:</b> Concrete object represents a bigger idea (e.g. "a road" as a symbol for choices).
                </p>
                <p>
                  <b>Allusion:</b> Reference to literature, history, or culture to deepen meaning.
                </p>
                <blockquote>
                  "Two roads diverged in a yellow wood..."<br />
                  <i>—Robert Frost</i>
                </blockquote>
                <SpeakButton text="Two roads diverged in a yellow wood." />
                <p>
                  <b>Question:</b> What might the roads symbolize? Where does the poem allude to a real-life dilemma?
                </p>
              </>
            )
          },
          {
            title: "Sound Devices: Beyond Rhyme",
            body: (
              <>
                <p>
                  <b>Alliteration:</b> Repetition of initial consonant sounds (<i>"wild winds weep"</i>)
                </p>
                <p>
                  <b>Assonance:</b> Repetition of vowel sounds (<i>"rise high in the bright sky"</i>)
                </p>
                <p>
                  <b>Consonance:</b> Repetition of consonant sounds (<i>"stroke of luck"</i>)
                </p>
                <p>
                  <b>Onomatopoeia:</b> Words that imitate sound (<i>"buzz", "hiss"</i>)
                </p>
                <p>
                  <b>Challenge:</b> Write a couplet using at least two sound devices.
                </p>
              </>
            )
          },
          {
            title: "Analyzing and Writing a Sonnet",
            body: (
              <>
                <p>
                  <b>Step 1:</b> Read a sonnet and break down the rhyme scheme, structure, and tone.
                </p>
                <p>
                  <b>Step 2:</b> Draft your own 14-line poem on any topic, following the sonnet rhyme scheme.
                </p>
                <blockquote>
                  Example quatrain: <br />
                  "Beneath the stars, a restless city glows,<br />
                  Its silent stories drifting on the air.<br />
                  Within the rush, a secret beauty grows,<br />
                  The dreamer finds a quiet comfort there."
                </blockquote>
                <SpeakButton text="Beneath the stars, a restless city glows, Its silent stories drifting on the air. Within the rush, a secret beauty grows, The dreamer finds a quiet comfort there." />
                <p>
                  <b>Reflection:</b> Where does your sonnet shift? What emotion does your volta reveal?
                </p>
              </>
            )
          },
          {
            title: "Practice: Deep Reading & Creative Response",
            body: (
              <>
                <p>
                  <b>Identify poetic devices:</b> Find alliteration, metaphor, or personification in the lines:
                  <blockquote>
                    "The midnight train sighs through sleeping towns,<br />
                    Shadows linger, weaving dreams of light."
                  </blockquote>
                  <SpeakButton text="The midnight train sighs through sleeping towns, Shadows linger, weaving dreams of light." />
                </p>
                <p>
                  <b>Creative Task:</b> Write an extended metaphor about change, using strong imagery and at least one sound device.
                </p>
              </>
            )
          }
        ],

        anthology: [
          {
            title: "Shakespearean Sonnet (Sonnet 18, excerpt)",
            lines: [
              "Shall I compare thee to a summer's day?",
              "Thou art more lovely and more temperate:",
              "Rough winds do shake the darling buds of May,",
              "And summer’s lease hath all too short a date;"
            ],
            author: "William Shakespeare"
          },
          {
            title: "Modern Free Verse",
            lines: [
              "In the rush of steel and trembling glass,",
              "A song of solitude threads through the night.",
              "City lights flicker, pulsing like restless hearts,",
              "Hope finds shelter in the quietest hour."
            ],
            author: "Student example"
          },
          {
            title: "Symbolic Imagery Example",
            lines: [
              "A single leaf drifts down the empty street,",
              "Its golden spin a silent song of change."
            ],
            author: "Student example"
          }
        ],

        generateQuiz: () => {
          const questionPool = [
            buildPoetryFormMC(poetryFormsPool),           // "Which form is this poem written in?"
            buildPoeticDeviceMC(poeticDevicesPool),       // "Which poetic device is used in: ...?"
            buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
            buildLineStanzaMC(lineStanzaPool),
            buildIambicPentameterMC(iambicPentameterPool),
            buildRhymeSchemeMC(rhymeSchemePool),
            // Sonnet specific question:
            {
              questionType: "MC",
              question: "How many lines does a Shakespearean sonnet have?",
              correctAnswer: "14",
              options: ["14", "12", "16", "10"]
            },
            {
              questionType: "MC",
              question: 'Which literary device is used in: "Hope is the thing with feathers"?',
              correctAnswer: "Metaphor",
              options: ["Metaphor", "Simile", "Personification", "Alliteration"]
            },
            // Open-ended writing:
            {
              questionType: "FITB",
              question: "Write a quatrain (4 lines) that describes the night, using at least one metaphor.",
              correctAnswer: null,
              checkType: "creative-quatrain",
              requiredWord: null
            }
          ].filter(Boolean);

          // Return up to 8 random questions
          return shuffleArray(questionPool).slice(0, 8);
        }
      }
    ]
  },
  grade10: {
    units: [
      {
        title: "Grade 10 Poetry Unit 1: Narrative Poetry & Voice",
        slides: [
          {
            title: "Narrative Poetry: Telling a Story",
            body: (
              <>
                <p>
                  <b>Narrative Poetry</b> uses verse to tell a story, with characters, setting, conflict, and resolution.
                  Classic examples include epics, ballads, and even some free verse.
                </p>
                <p>
                  <b>Example:</b>
                  <i>
                    "He clasps the crag with crooked hands;<br/>
                    Close to the sun in lonely lands..."<br/>
                  </i>
                  <SpeakButton text="He clasps the crag with crooked hands; Close to the sun in lonely lands." />
                  <i>—Tennyson, “The Eagle”</i>
                </p>
              </>
            )
          },
          {
            title: "Persona & Voice",
            body: (
              <>
                <p>
                  <b>Persona:</b> The speaker is not always the poet! Poets adopt different voices for effect.
                </p>
                <blockquote>
                  "Because I could not stop for Death –<br/>
                  He kindly stopped for me –"<br/>
                  <i>—Emily Dickinson</i>
                </blockquote>
                <SpeakButton text="Because I could not stop for Death – He kindly stopped for me –" />
                <p>
                  <b>Analysis:</b> How does the poet use voice/persona to shape our emotions or understanding?
                </p>
              </>
            )
          },
          {
            title: "Complex Forms: Villanelle, Sestina",
            body: (
              <>
                <p>
                  <b>Villanelle:</b> 19 lines with two repeating rhymes and refrains (see “Do Not Go Gentle Into That Good Night” by Dylan Thomas).
                </p>
                <p>
                  <b>Sestina:</b> Six stanzas of six lines each, with word repetition at line-ends in a set pattern.
                </p>
                <p>
                  <b>Challenge:</b> Find or attempt a stanza in a complex form.
                </p>
              </>
            )
          },
          {
            title: "Theme, Subtext & Social Commentary",
            body: (
              <>
                <p>
                  <b>Theme:</b> Poems in this unit may address social issues, history, or identity.
                </p>
                <p>
                  <b>Subtext:</b> What does the poem suggest beneath the surface? What is left unsaid?
                </p>
                <blockquote>
                  "I, too, sing America."<br/>
                  <i>—Langston Hughes</i>
                </blockquote>
                <SpeakButton text="I, too, sing America." />
              </>
            )
          },
          {
            title: "Practice: Deep Analysis & Creative Response",
            body: (
              <>
                <p>
                  <b>Find the voice:</b> Who is really speaking in “The Eagle”? Is it the bird, the poet, or something else?
                </p>
                <p>
                  <b>Creative Task:</b> Write a 10-line narrative poem from an unexpected persona (object, animal, etc.).
                </p>
              </>
            )
          }
        ],

        anthology: [
          {
            title: "Villanelle Example (excerpt)",
            lines: [
              "Do not go gentle into that good night,",
              "Old age should burn and rave at close of day;",
              "Rage, rage against the dying of the light."
            ],
            author: "Dylan Thomas"
          },
          {
            title: "Modern Narrative (excerpt)",
            lines: [
              "At the corner of the street,",
              "A song floats from an open window,",
              "Carrying memories of distant summers."
            ],
            author: "Student example"
          }
        ],

        generateQuiz: () => {
          const questionPool = [
            buildPoetryFormMC(poetryFormsPool),
            buildPoeticDeviceMC(poeticDevicesPool),
            buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
            buildLineStanzaMC(lineStanzaPool),
            buildRhymeSchemeMC(rhymeSchemePool),
            {
              questionType: "MC",
              question: "What is a villanelle?",
              correctAnswer: "A 19-line poem with two refrains and a set rhyme scheme",
              options: [
                "A 19-line poem with two refrains and a set rhyme scheme",
                "A 14-line poem about love",
                "A poem with six unrhymed stanzas",
                "A poem that tells a story in prose"
              ]
            },
            {
              questionType: "MC",
              question: "In 'Because I could not stop for Death,' who is the poem’s persona?",
              correctAnswer: "A speaker imagining their own death",
              options: [
                "A speaker imagining their own death",
                "The poet herself",
                "A ghost",
                "Death personified"
              ]
            },
            {
              questionType: "FITB",
              question: "Write a short stanza (4-6 lines) as the voice of an inanimate object.",
              correctAnswer: null,
              checkType: "creative-persona",
              requiredWord: null
            }
          ].filter(Boolean);
          return shuffleArray(questionPool).slice(0, 8);
        }
      }
    ]
  },

  grade11: {
    units: [
      {
        title: "Grade 11 Poetry Unit 1: Modernism, Imagism & Political Voice",
        slides: [
          {
            title: "Modernist Poetry: Breaking Conventions",
            body: (
              <>
                <p>
                  <b>Modernism:</b> Early 20th-century movement rejecting strict meter, embracing ambiguity, and challenging tradition.
                </p>
                <blockquote>
                  "The apparition of these faces in the crowd;<br/>
                  Petals on a wet, black bough."<br/>
                  <i>—Ezra Pound, “In a Station of the Metro”</i>
                </blockquote>
                <SpeakButton text="The apparition of these faces in the crowd; Petals on a wet, black bough." />
                <p>
                  <b>Challenge:</b> How does the poem use juxtaposition and imagery?
                </p>
              </>
            )
          },
          {
            title: "Imagism & Concrete Imagery",
            body: (
              <>
                <p>
                  <b>Imagism:</b> Focuses on clarity, economy of language, and precise visual images. Less is more.
                </p>
                <blockquote>
                  "so much depends<br/>
                  upon<br/>
                  a red wheel<br/>
                  barrow<br/>
                  glazed with rain<br/>
                  water<br/>
                  beside the white<br/>
                  chickens"<br/>
                  <i>—William Carlos Williams</i>
                </blockquote>
                <SpeakButton text="so much depends upon a red wheel barrow glazed with rain water beside the white chickens" />
                <p>
                  <b>Analysis:</b> What does the image suggest about ordinary life?
                </p>
              </>
            )
          },
          {
            title: "Poetry & Political Voice",
            body: (
              <>
                <p>
                  <b>Political Poetry:</b> How do poets use verse to protest, persuade, or reveal injustice?
                </p>
                <blockquote>
                  "Let America be America again.<br/>
                  Let it be the dream it used to be."<br/>
                  <i>—Langston Hughes</i>
                </blockquote>
                <SpeakButton text="Let America be America again. Let it be the dream it used to be." />
                <p>
                  <b>Challenge:</b> What issue is the poem addressing? How is voice used to persuade?
                </p>
              </>
            )
          },
          {
            title: "Intertextuality & Allusion",
            body: (
              <>
                <p>
                  <b>Intertextuality:</b> Referencing other literary works to add meaning.
                </p>
                <blockquote>
                  "April is the cruellest month, breeding<br/>
                  Lilacs out of the dead land..."<br/>
                  <i>—T.S. Eliot, “The Waste Land”</i>
                </blockquote>
                <SpeakButton text="April is the cruellest month, breeding lilacs out of the dead land..." />
                <p>
                  <b>Practice:</b> Find an allusion in a contemporary poem or song.
                </p>
              </>
            )
          },
          {
            title: "Practice: Modernist and Political Poetry",
            body: (
              <>
                <p>
                  <b>Creative Task:</b> Write a short modernist poem (5-8 lines) using concrete imagery and no set rhyme scheme.
                </p>
                <p>
                  <b>Analysis:</b> Find and explain an allusion or political message in a given poem.
                </p>
              </>
            )
          }
        ],

        anthology: [
          {
            title: "Imagist Modernism",
            lines: [
              "In the silent garden,",
              "A single flower remains—",
              "Memory’s echo."
            ],
            author: "Student example"
          },
          {
            title: "Political Voice (excerpt)",
            lines: [
              "We rise with one voice,",
              "Broken yet unbowed,",
              "Planting hope in every line of verse."
            ],
            author: "Student example"
          }
        ],

        generateQuiz: () => {
          const questionPool = [
            buildPoetryFormMC(poetryFormsPool),
            buildPoeticDeviceMC(poeticDevicesPool),
            buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
            buildLineStanzaMC(lineStanzaPool),
            buildRhymeSchemeMC(rhymeSchemePool),
            {
              questionType: "MC",
              question: "What defines Imagist poetry?",
              correctAnswer: "Precise imagery and economy of language",
              options: [
                "Precise imagery and economy of language",
                "A strict rhyme scheme",
                "Use of classical mythology",
                "Confessional tone"
              ]
            },
            {
              questionType: "MC",
              question: "What is an allusion in poetry?",
              correctAnswer: "A reference to another work, person, or event",
              options: [
                "A repeated line in a poem",
                "A rhyme pattern",
                "A reference to another work, person, or event",
                "A metaphor extended through the poem"
              ]
            },
            {
              questionType: "FITB",
              question: "Write a free verse poem about change, using at least one vivid image.",
              correctAnswer: null,
              checkType: "creative-image",
              requiredWord: null
            }
          ].filter(Boolean);
          return shuffleArray(questionPool).slice(0, 8);
        }
      }
    ]
  },

  grade12: {
    units: [
      {
        title: "Grade 12 Poetry Unit 1: Experimental Forms & Literary Critique",
        slides: [
          {
            title: "Postmodern & Experimental Poetry",
            body: (
              <>
                <p>
                  <b>Postmodernism:</b> Blurs boundaries—mixes genres, plays with structure, and questions meaning.
                </p>
                <p>
                  <b>Experimental Forms:</b> Concrete poetry, visual layouts, found poems, and multimedia texts.
                </p>
                <blockquote>
                  "[A visual poem shaped like a tree]"<br/>
                  <i>—E.E. Cummings, “l(a”</i>
                </blockquote>
                <SpeakButton text="l(a" />
                <p>
                  <b>Question:</b> How does the form itself create meaning?
                </p>
              </>
            )
          },
          {
            title: "Critique, Theory & Meta-Poetry",
            body: (
              <>
                <p>
                  <b>Literary Critique:</b> Analyze how poems construct (and deconstruct) meaning.
                </p>
                <p>
                  <b>Meta-Poetry:</b> Poems about poetry itself, or questioning language and authorship.
                </p>
                <blockquote>
                  "This is just to say<br/>
                  I have eaten<br/>
                  the plums<br/>
                  that were in<br/>
                  the icebox"<br/>
                  <i>—William Carlos Williams</i>
                </blockquote>
                <SpeakButton text="This is just to say I have eaten the plums that were in the icebox" />
              </>
            )
          },
          {
            title: "Global Voices & Translation",
            body: (
              <>
                <p>
                  <b>World Poetry:</b> Contemporary poems from diverse cultures; the art of translation.
                </p>
                <p>
                  <b>Challenge:</b> Compare a poem and its translation. What changes? What stays universal?
                </p>
                <blockquote>
                  "Even the silence / has something to say tonight"<br/>
                  <i>—Student translation, original author unknown</i>
                </blockquote>
                <SpeakButton text="Even the silence has something to say tonight" />
              </>
            )
          },
          {
            title: "Practice: Experiment, Critique & Reflect",
            body: (
              <>
                <p>
                  <b>Creative Task:</b> Create a visual or found poem using unexpected materials or digital tools.
                </p>
                <p>
                  <b>Critique:</b> Write a short analysis of how form and content interact in a poem of your choice.
                </p>
                <p>
                  <b>Discussion:</b> Should all poetry “make sense”? Why or why not?
                </p>
              </>
            )
          }
        ],

        anthology: [
          {
            title: "Concrete/Visual Poetry",
            lines: [
              "[a visual layout—tree, spiral, etc.]"
            ],
            author: "Student example"
          },
          {
            title: "Meta-Poetry Example",
            lines: [
              "These words undo themselves as you read,",
              "Ink dissolves meaning, leaving possibility."
            ],
            author: "Student example"
          }
        ],

        generateQuiz: () => {
          const questionPool = [
            buildPoetryFormMC(poetryFormsPool),
            buildPoeticDeviceMC(poeticDevicesPool),
            buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
            buildLineStanzaMC(lineStanzaPool),
            buildRhymeSchemeMC(rhymeSchemePool),
            {
              questionType: "MC",
              question: "What is 'meta-poetry'?",
              correctAnswer: "Poetry about poetry itself",
              options: [
                "Poetry about poetry itself",
                "Poetry with a strict rhyme",
                "Poetry written by multiple authors",
                "A form with 14 lines"
              ]
            },
            {
              questionType: "MC",
              question: "Which is a hallmark of postmodern poetry?",
              correctAnswer: "Blurring genres and playing with form",
              options: [
                "Blurring genres and playing with form",
                "Strict adherence to tradition",
                "Always using rhyme",
                "Ignoring visual layout"
              ]
            },
            {
              questionType: "FITB",
              question: "Create a visual poem (describe your layout and theme in words if you cannot draw).",
              correctAnswer: null,
              checkType: "creative-visual",
              requiredWord: null
            }
          ].filter(Boolean);
          return shuffleArray(questionPool).slice(0, 8);
        }
      }
    ]
  }
};


// ==================== FINAL QUIZ GENERATION ==================== //

/**
 * If you want a "global final quiz" that covers everything in PoetryModule,
 * you can combine multiple pools for a more comprehensive test.
 */
function buildPoetryFinalQuiz(currentGrade = "grade9") {
    // Core device/form questions (Grade 8+)
    let bigSet = [
      buildPoetryFormMC(poetryFormsPool),
      buildPoeticDeviceMC(poeticDevicesPool),
      buildIdentifyPoeticDeviceQ(identifyPoeticDevicesPool),
      buildLineStanzaMC(lineStanzaPool),
      buildIambicPentameterMC(iambicPentameterPool),
      buildRhymeSchemeMC(rhymeSchemePool),
      {
        questionType: "MC",
        question: 'Which poetic device is used in: "The sun smiled on the fields"?',
        correctAnswer: "Personification",
        options: ["Metaphor", "Personification", "Alliteration", "Onomatopoeia"]
      },
      {
        questionType: "FITB",
        question: "Compose a haiku (5-7-5 syllable structure) that includes a seasonal reference.",
        correctAnswer: null,
        checkType: "haiku",
        requiredWord: null
      },
      {
        questionType: "MC",
        question: "Which line is an example of iambic pentameter?",
        correctAnswer: "Shall I compare thee to a summer's day?",
        options: [
          "Green fields under the quiet sky.",
          "Shall I compare thee to a summer's day?",
          "My dog is friendly, brown, and loves to play.",
          "Once upon a midnight dreary, while I pondered, weak and weary."
        ]
      }
    ];
  
    // Add Grade 9+ (sonnet, metaphor, and analysis)
    if (
      currentGrade === "grade9" ||
      currentGrade === "grade10" ||
      currentGrade === "grade11" ||
      currentGrade === "grade12"
    ) {
      bigSet = bigSet.concat([
        {
          questionType: "MC",
          question: "How many lines does a Shakespearean sonnet have?",
          correctAnswer: "14",
          options: ["14", "12", "10", "16"]
        },
        {
          questionType: "MC",
          question: "What is a 'volta' in a sonnet?",
          correctAnswer: "A shift or turn in the poem's argument or mood",
          options: [
            "A shift or turn in the poem's argument or mood",
            "A rhyme pattern in the final lines",
            "A repeated word at the end of every stanza",
            "A line that uses alliteration"
          ]
        },
        {
          questionType: "MC",
          question: 'Which literary device is used in: "Hope is the thing with feathers"?',
          correctAnswer: "Metaphor",
          options: ["Metaphor", "Simile", "Personification", "Alliteration"]
        },
        {
          questionType: "IDENTIFY",
          question:
            "Type the extended metaphor in this excerpt: 'All the world's a stage, and all the men and women merely players.'",
          correctAnswers: ["All the world's a stage, and all the men and women merely players."]
        },
        {
          questionType: "FITB",
          question: "Write four lines of a sonnet (quatrain) on a modern topic, following ABAB rhyme and using one metaphor.",
          correctAnswer: null,
          checkType: "creative-quatrain",
          requiredWord: null
        }
      ]);
    }
  
    // Add Grade 10+ (narrative/voice/forms)
    if (
      currentGrade === "grade10" ||
      currentGrade === "grade11" ||
      currentGrade === "grade12"
    ) {
      bigSet = bigSet.concat([
        {
          questionType: "MC",
          question: "What is a villanelle?",
          correctAnswer: "A 19-line poem with two refrains and a set rhyme scheme",
          options: [
            "A 19-line poem with two refrains and a set rhyme scheme",
            "A 14-line poem about love",
            "A poem with six unrhymed stanzas",
            "A poem that tells a story in prose"
          ]
        },
        {
          questionType: "MC",
          question: "What is meant by 'persona' in poetry?",
          correctAnswer: "The voice adopted by the poet, not always themselves",
          options: [
            "The voice adopted by the poet, not always themselves",
            "A recurring rhyme pattern",
            "The main character in a narrative poem",
            "A type of stanza"
          ]
        },
        {
          questionType: "FITB",
          question: "Write a stanza in the voice of a historical figure, using at least one metaphor.",
          correctAnswer: null,
          checkType: "creative-persona",
          requiredWord: null
        }
      ]);
    }
  
    // Add Grade 11+ (modernism, imagism, political/allusion)
    if (
      currentGrade === "grade11" ||
      currentGrade === "grade12"
    ) {
      bigSet = bigSet.concat([
        {
          questionType: "MC",
          question: "What defines Imagist poetry?",
          correctAnswer: "Precise imagery and economy of language",
          options: [
            "Precise imagery and economy of language",
            "A strict rhyme scheme",
            "Use of classical mythology",
            "Confessional tone"
          ]
        },
        {
          questionType: "MC",
          question: "What is an allusion in poetry?",
          correctAnswer: "A reference to another work, person, or event",
          options: [
            "A repeated line in a poem",
            "A rhyme pattern",
            "A reference to another work, person, or event",
            "A metaphor extended through the poem"
          ]
        },
        {
          questionType: "FITB",
          question: "Write a free verse poem (6–10 lines) about a contemporary issue using at least one vivid image and allusion.",
          correctAnswer: null,
          checkType: "creative-modernism",
          requiredWord: null
        }
      ]);
    }
  
    // Add Grade 12+ (postmodernism, experimental, critique, global/translation)
    if (currentGrade === "grade12") {
      bigSet = bigSet.concat([
        {
          questionType: "MC",
          question: "What is 'meta-poetry'?",
          correctAnswer: "Poetry about poetry itself",
          options: [
            "Poetry about poetry itself",
            "Poetry with a strict rhyme",
            "Poetry written by multiple authors",
            "A form with 14 lines"
          ]
        },
        {
          questionType: "MC",
          question: "Which is a hallmark of postmodern poetry?",
          correctAnswer: "Blurring genres and playing with form",
          options: [
            "Blurring genres and playing with form",
            "Strict adherence to tradition",
            "Always using rhyme",
            "Ignoring visual layout"
          ]
        },
        {
          questionType: "FITB",
          question: "Describe how a poem’s visual layout can affect its meaning. (Give an example or make up your own visual poem description.)",
          correctAnswer: null,
          checkType: "creative-visual",
          requiredWord: null
        },
        {
          questionType: "FITB",
          question: "Choose a poem from another culture or language. What is lost and what is preserved in translation?",
          correctAnswer: null,
          checkType: "creative-translation",
          requiredWord: null
        }
      ]);
    }
  
    // Shuffle and select up to 12 questions
    return shuffleArray(bigSet).slice(0, 12);
  }
  
  


// ==================== MAIN COMPONENT ==================== //

export default function PoetryModule() {
  const [currentGrade, setCurrentGrade] = useState("grade8");
  const [unitIdx, setUnitIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);

  // Unit quiz states
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  // Final quiz states
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);  // 5-minute timer, for example
  const hasAwardedTokens = useRef(false);

  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("loggedInUser"));
    if (savedUser) setUser(savedUser);
  }, []);

  // Timer for final quiz if desired
  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);

  // Get current unit
  const currentUnit = poetryCurriculum[currentGrade]?.units[unitIdx];
  const slides = currentUnit?.slides || [];


  // -------------- UNIT QUIZ LOGIC --------------
  function startUnitQuiz() {
    if (!currentUnit?.generateQuiz) return;
    const newQuiz = currentUnit.generateQuiz();
    setQuiz(newQuiz);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }

  function handleUnitAnswer(opt) {
    const cQ = quiz[qIdx];

    // Minimal correctness logic
    if (cQ.questionType === "MC" || cQ.questionType === "MC_MULTI") {
      if (opt === cQ.correctAnswer) {
        setQScore(s => s + 1);
      }
    } else if (cQ.questionType === "FITB" || cQ.questionType === "IDENTIFY") {
      // In advanced usage, check user’s input for correctness.
      // For now, we increment score automatically to let them practice.
      setQScore(s => s + 1);
    }

    nextUnitQuestion();
  }

  function nextUnitQuestion() {
    if (qIdx < quiz.length - 1) {
      setQIdx(i => i + 1);
    } else {
      setQuizFin(true);
    }
  }

  function resetUnitQuiz() {
    setShowQuiz(false);
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
  }

  function renderUnitQuiz() {
    if (quizFin) {
      return (
        <div className="quiz-result-screen">
          <h3>Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={resetUnitQuiz}>Return to Lessons</button>
        </div>
      );
    }

    const cQ = quiz[qIdx];
    if (!cQ) return <p>Loading quiz...</p>;

    switch (cQ.questionType) {
      case "MC":
        return (
          <StoryModeMultipleChoice
            cQ={cQ}
            onAnswerClick={handleUnitAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
          />
        );
      case "MC_MULTI":
        return (
          <StoryModeMultiPart
            cQ={cQ}
            onSubmit={handleUnitAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
          />
        );
      case "FITB":
        return (
          <StoryModeFillInBlank
            cQ={cQ}
            onSubmit={handleUnitAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
          />
        );
      case "IDENTIFY":
        return (
          <StoryModeIdentify
            cQ={cQ}
            onSubmit={handleUnitAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
          />
        );
      default:
        return <p>Unknown question type</p>;
    }
  }


  // -------------- FINAL QUIZ LOGIC --------------
  function startFinalQuiz() {
    const finalSet = buildPoetryFinalQuiz(currentGrade);
    setFinalQs(finalSet);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(300);
    setShowFinal(true);
  }

  function handleFinalAnswer(opt) {
    const cQ = finalQs[fIdx];
    if (!cQ) return;

    // Minimal correctness logic
    if (cQ.questionType === "MC" || cQ.questionType === "MC_MULTI") {
      if (opt === cQ.correctAnswer) {
        setFScore(s => s + 1);
      }
    } else if (cQ.questionType === "FITB" || cQ.questionType === "IDENTIFY") {
      // More detailed check could go here
      setFScore(s => s + 1);
    }
    nextFinalQuestion();
  }

  function nextFinalQuestion() {
    if (fIdx < finalQs.length - 1) {
      setFIdx(i => i + 1);
    } else {
      setFFin(true);
    }
  }

  // After final quiz ends, optionally award tokens or show results
  useEffect(() => {
    if (fFin && showFinal && !hasAwardedTokens.current) {
      // e.g., require 70% for a passing score
      const percent = Math.round((fScore / finalQs.length) * 100);
      alert(`Final Quiz Completed! Score: ${fScore}/${finalQs.length} (${percent}%)`);
      // Optionally do something if pass:
      // if (percent >= 70) { ... award token ... }
      hasAwardedTokens.current = true;
    }
  }, [fFin, showFinal, fScore, finalQs.length]);

  function renderFinalQuiz() {
    if (fFin) {
      return (
        <div className="quiz-result-screen">
          <h3>Poetry Final Quiz Finished!</h3>
          <p>Your final score: {fScore} / {finalQs.length}.</p>
          <button className="button1" onClick={() => setShowFinal(false)}>Return to Poetry Module</button>
        </div>
      );
    }

    const cQ = finalQs[fIdx];
    if (!cQ) return <p>Loading final quiz...</p>;

    return (
      <FinalQuiz
        cQ={cQ}
        fIdx={fIdx}
        finalQs={finalQs}
        handleFinalAnswer={handleFinalAnswer}
        fScore={fScore}
        grade={currentGrade}
        timeLeft={timeLeft}
      />
    );
  }


  // -------------- LESSON SLIDE RENDER --------------
  function renderSlide() {
    const slide = slides[slideIdx];
    if (!slide) return <p>No slides found for this unit.</p>;

    const isLastSlide = slideIdx === slides.length - 1;

    return (
      <div>
        <h2>{slide.title}</h2>
        <div>{slide.body}</div>

        <div style={{ marginTop: 10 }}>
          {slideIdx > 0 && (
            <button className="button1" onClick={() => setSlideIdx(i => i - 1)}>
              Back
            </button>
          )}
          {!isLastSlide && (
            <button
              className="button1"
              style={{ marginLeft: 10 }}
              onClick={() => setSlideIdx(i => i + 1)}
            >
              Next
            </button>
          )}
          {isLastSlide && (
            <button
              className="button1"
              style={{ marginLeft: 10 }}
              onClick={startUnitQuiz}
            >
              Take Unit Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  // -------------- MAIN RENDER --------------
  return (
    <div className="poetry-module-page">
      <h1>Comprehensive Poetry Curriculum (Grades 8–12)</h1>

      {/* Grade Selector */}
      <div style={{ margin: "10px 0" }}>
        <label>Choose Grade: </label>
        <select
          className="button1"
          value={currentGrade}
          onChange={e => {
            setCurrentGrade(e.target.value);
            setUnitIdx(0);
            setSlideIdx(0);
            resetUnitQuiz();
            setShowFinal(false);
          }}
        >
          <option value="grade8">Grade 8</option>
          <option value="grade9">Grade 9</option>
          <option value="grade10">Grade 10</option>
          <option value="grade11">Grade 11</option>
          <option value="grade12">Grade 12</option>
        </select>
      </div>

      {/* Unit Selector (if multiple units per grade) */}
      {poetryCurriculum[currentGrade]?.units?.length > 1 && (
        <div style={{ marginBottom: "10px" }}>
          <label>Select Unit: </label>
          <select
            className="button1"
            value={unitIdx}
            onChange={e => {
              setUnitIdx(parseInt(e.target.value, 10));
              setSlideIdx(0);
              resetUnitQuiz();
              setShowFinal(false);
            }}
          >
            {poetryCurriculum[currentGrade].units.map((u, idx) => (
              <option key={idx} value={idx}>
                {u.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Render either the slides, the unit quiz, the final quiz */}
      {!showQuiz && !showFinal && renderSlide()}
      {showQuiz && !showFinal && renderUnitQuiz()}
      {showFinal && renderFinalQuiz()}

      {/* Button to start the global final quiz (for the entire poetry module) */}
      {!showQuiz && !showFinal && (
        <div style={{ margin: "20px 0" }}>
          <button className="button1" onClick={startFinalQuiz}>
            Take Final Poetry Quiz
          </button>
        </div>
      )}

      {/* Link back */}
      <Link to="/english" className="button1">
        ← Back to English Page
      </Link>
    </div>
  );
}
